import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    linkedSignal,
    model,
    resource,
    signal,
    Signal,
    untracked,
    viewChild,
} from "@angular/core";
import { HttpRequestOptions } from "../../dataaccess/data.access";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { CalendarService } from "../../modules/home/calendar/calendar.service";
import { CalendarData } from "../../modules/home/calendar/CalendarData";
import { OptBean } from "../../modules/s25-opt/s25.opt.component";
import { S25OptModule } from "../../modules/s25-opt/s25.opt.module";
import { OptUtil, OptUtilI } from "../../modules/s25-opt/s25.opt.util";
import { Item } from "../../pojo/Item";
import { EventService } from "../../services/event.service";
import { EmbeddingUtil } from "../../util/embedding.util";
import { s25Resource } from "../../util/s25.resource";
import { Calendar } from "./calendar.component";
import { S25CalendarStore } from "./calendar.store";

type OptBeanGenerator = (refreshF: any, runTimeParams: Partial<OptBean>) => Promise<OptUtilI>;

export interface CalendarOptModelBean {
    itemId?: number;
    itemTypeId?: Item.Id;
    searchQuery?: string;
    // TODO: delete this when qlSearch is moved to Angular
    refreshListeners?: Array<(beanUpdates: Partial<Omit<CalendarOptModelBean, "refreshListeners">>) => void>;
    /**
     * This is currently being passed in from qlSearch as an alternative way to get the search query.
     * TODO: consider merging the two
     */
    tempSearchFn?: () => Promise<{ invalidSearch: boolean; searchQuery: string }>;
    getdata?: (params: CalendarService.DataParams, httpParams?: HttpRequestOptions) => Promise<{ root: CalendarData }>;
    messages?: { loading: string; message: string };
    isauth?: { value: boolean };
    optBean?: OptBean;
    optBeanRunTime?: {
        date?: Date;
        searchContext?: string;
        itemId?: number;
        itemTypeId?: Item.Id;
        compsubject?: string;
        helpTopic?: string;
    };
}

@TypeManagerDecorator("s25-ng-calendar-opt")
@Component({
    selector: "s25-ng-calendar-opt",
    standalone: true,
    template: `
        @if (errorMessage() || optBean()) {
            <s25-ng-opt
                [modelBean]="optBean()"
                (modelValueChange)="updateOptBean($event)"
                (dateChange)="updateOptBean()"
                (endDateChange)="updateOptBean()"
                (dateViewChange)="updateOptBean()"
                (daysChange)="updateOptBean()"
                (includeRequestedChange)="updateOptBean()"
                (refreshed)="updateOptBean()"
                (modeChange)="updateOptBean()"
                (snapToGridChange)="updateOptBean()"
                (utilizationViewChange)="updateOptBean()"
                (queryChange)="updateOptBean()"
                (tabChange)="updateOptBean()"
                (columnChooserChange)="updateOptBean()"
                (editModeChange)="updateOptBean()"
                (initialized)="optBarInitialized.set(true)"
            />
            @if (!sourceOptBean.resource.error()) {
                <s25-ng-calendar
                    class="ngSize100"
                    [isLoading]="!optBarInitialized()"
                    [comptype]="comptype()"
                    [compsubject]="compsubject()"
                    [searchQuery]="modelBean()?.searchQuery"
                    [date]="optBean()?.date"
                    [dataFn]="getData()"
                    [multiQueryStr]="optBean()?.multiQueryStr"
                    [useCache]="optBean()?.useCache"
                    [includeAddtl]="optBean()?.includeAddtl"
                    [showRelatedEvents]="optBean()?.showRelatedEvents"
                    [showRelatedLocations]="optBean()?.showRelatedLocations"
                    [datePropertyValue]="optBean()?.datePropertyValue"
                    [endDate]="optBean()?.endDate"
                    [chosenVal]="optBean()?.chosen?.obj.val"
                    [showBlackouts]="optBean()?.showBlackouts"
                    (updated)="setLastUpdateDate($event)"
                    (previousPage)="previousPage()"
                    (nextPage)="nextPage()"
                />
            } @else {
                <div class="NoInformation">
                    <span>{{ errorMessage() }}</span>
                </div>
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [Calendar, S25OptModule],
    providers: [S25CalendarStore],
})
export class CalendarWithOptions {
    readonly initialCompType = input.required<OptUtil.CompType>({
        alias: "comptype",
    });
    readonly initialCompSubject = input.required<Item.Subject>({
        alias: "compsubject",
    });
    readonly modelBean = model.required<CalendarOptModelBean>();

    readonly calendar = viewChild(Calendar);

    private readonly store = inject(S25CalendarStore);
    private readonly elementRef = inject(ElementRef);
    private readonly embeddingUtil = EmbeddingUtil.getSingleton();

    protected readonly optBarInitialized = signal(false);
    private readonly providedOptBeanRunTime = computed(() => this.modelBean()?.optBeanRunTime);
    protected readonly sourceOptBean = s25Resource({
        params: () => ({
            optBeanRunTime: {
                ...(this.providedOptBeanRunTime() || {}),
                compsubject: this.compsubject(),
                ...(this.comptype() === "calendar"
                    ? {
                          helpTopic: {
                              event: "view_myevents_calendar",
                              location: "view_loc_calendar",
                              organization: "view_org_calendar",
                              resource: "view_res_calendar",
                          }[<string>this.compsubject()],
                      }
                    : {}),
            },
            optBeanGenerator: this.OptBeanGenerators[this.comptype()] ?? undefined,
        }),
        loader: ({ params }): Promise<OptUtilI> =>
            params.optBeanGenerator?.(() => this.calendar()?.refresh(), params.optBeanRunTime) ??
            Promise.reject(undefined),
    });
    readonly compsubject = linkedSignal(() => this.initialCompSubject());
    readonly comptype = linkedSignal(() => this.initialCompType());
    // jenky way of overriding the chosen given to us by the OptBeanGenerator
    readonly chosen = signal(undefined);
    /** This is the optBean for s25-ng-opt that will be mutated >:( */
    // TODO: update s25-ng-opt to accept granular inputs so we can abandon the omni-object
    // WARNING: using optBean in any signals that can indirectly update optBean will cause infinite loops
    readonly optBean = linkedSignal<OptBean | undefined>(
        () =>
            this.sourceOptBean.value() && {
                ...this.sourceOptBean.value(),
                ...untracked(() => (this.chosen() ? { chosen: this.chosen() } : {})),
            },
    );
    readonly errorMessage = computed(() => this.sourceOptBean.resource.error()?.message);
    readonly getData = computed((): CalendarOptModelBean["getdata"] => {
        const getData = this.modelBean()?.getdata ?? CalendarService.getData;
        const tempSearchFn = this.modelBean()?.tempSearchFn;
        if (tempSearchFn) {
            return async (
                params: CalendarService.DataParams,
                httpParams?: HttpRequestOptions,
            ): Promise<{ root: CalendarData }> => {
                const { invalidSearch, searchQuery } = await tempSearchFn();
                if (invalidSearch) {
                    return Promise.reject(new Error("Invalid search " + (searchQuery ?? "")));
                } else {
                    return getData(
                        {
                            ...params,
                            modelBean: {
                                ...params.modelBean,
                                searchQuery: searchQuery ?? params.modelBean.searchQuery,
                            },
                        },
                        httpParams,
                    );
                }
            };
        } else {
            return getData;
        }
    });

    constructor() {
        // effects that set signals are a smell, but this is necessary because our primary way of detecting
        // changes is through the omni-object
        effect(() => {
            if (this.optBean()?.comptype) {
                this.comptype.set(this.optBean().comptype);
            }
        });
        effect(() => {
            if (this.optBean()?.chosen) {
                this.chosen.set(this.optBean().chosen);
            }
        });
        effect(() => {
            if (this.optBean()?.compsubject) {
                this.compsubject.set(this.optBean().compsubject);
            }
        });
        effect(() => {
            if (this.sourceOptBean.value()) {
                this.embeddingUtil?.registerHeightManager(this.elementRef.nativeElement, null, 120, 1000);
            }
        });

        effect(() => this.store.setCompsubject(this.compsubject()));
        effect(() => this.store.setComptype(this.comptype()));
        effect(() => this.store.setModelBean(this.modelBean()));
        effect(() => this.store.setOptBean(this.optBean()));
        effect((onCleanup) => {
            // I know this is hacky, but it won't change. we need to refactor it with QL Search anyway
            const listeners = untracked(() => this.modelBean()?.refreshListeners);
            if (!listeners) {
                return;
            }
            const refreshSearch = (beanUpdates: {
                tempSearchFn?: () => Promise<{ invalidSearch: boolean; searchQuery: string }>;
                searchQuery?: string;
            }) => {
                this.modelBean.update((bean) => ({ ...bean, ...beanUpdates }));
            };
            listeners.push(refreshSearch);
            onCleanup(() => {
                const index = listeners.indexOf(refreshSearch);
                if (index > -1) {
                    listeners.splice(index, 1);
                }
            });
        });
    }

    public updateOptBean(optBean?: OptBean | undefined) {
        // trigger change detection if something changes lol
        const sourceBean = optBean ?? this.optBean();
        this.optBean.set(sourceBean ? { ...sourceBean } : undefined);
    }

    public previousPage() {
        this.optBean()?.updateDateArrow("back");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    public nextPage() {
        this.optBean()?.updateDateArrow("forward");
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    OptBeanGenerators: { [key in OptUtil.CompType]: OptBeanGenerator } = {
        home: OptUtil.formHomeCalendarBean,
        cal_event: async (refreshF, params) =>
            OptUtil.formEventDetailsCalendarBean(refreshF, {
                ...params,
                date: params.date ?? new Date((await EventService.getEventDates(params.itemId)).start_dt),
            }),
        cal_location: OptUtil.formLocationDetailsCalendarBean,
        cal_resource: OptUtil.formResourceDetailsCalendarBean,
        cal_organization: OptUtil.formOrganizationDetailsCalendarBean,
        cal_contact: OptUtil.formContactDetailsCalendarBean,
        cal_task: undefined,
        cal_task_search: OptUtil.formTaskSearchCalendarBean,
        calendar: OptUtil.formSearchCalendarBean,
    };

    public setLastUpdateDate(date: OptUtilI["lastupdate"]) {
        // TODO: ideally, this would be passed in as a property to s25-ng-opt,
        //  or the bean would be a computed signal
        this.optBean.update((bean) => ({
            ...bean,
            lastupdate: date,
        }));
    }
}
